import React from 'react';
import {Icon} from '../Icon';
import {Icons} from '@/constants';
import {cn} from '@/utils/common_functions';
import {Text} from '../Text';
import {Loader2} from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  leftIcon?: keyof typeof Icons;
  rightIcon?: keyof typeof Icons;
  iconClassName?: string;
  children?: React.ReactNode; // allow passing custom elements
  className?: string;
  textClassName?: string;
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  variant = 'primary',
  size = 'md',
  disabled = false,
  leftIcon,
  rightIcon,
  iconClassName = '',
  className,
  textClassName,
  isLoading = false,
  ...rest
}) => {
  // Tailwind classes for size
  const sizeStyles: Record<ButtonSize, string> = {
    sm: 'px-3 py-1 text-sm rounded-md',
    md: 'px-4 py-2 text-base rounded-md',
    lg: 'px-6 py-3 text-lg rounded-lg',
  };

  // Tailwind classes for variant
  const variantStyles: Record<ButtonVariant, string> = {
    primary: disabled
      ? 'bg-ui_disabled cursor:not-allowed text-white cursor-not-allowed'
      : 'bg-gradient-to-br from-ui_blue to-ui_blue_light text-white hover:from-ui_blue hover:to-ui_blue_light',
    secondary: disabled
      ? 'text-gray-500 border border-ui_grey cursor-not-allowed'
      : 'border border-ui_blue text-ui_blue hover:bg-ui_blue_light/10 gradient-text text-nowrap text-ui_blue',
  };

  return (
    <button
    type="button"
      className={cn(
        'relative flex items-center justify-center gap-2 font-PoppinsMedium shadow-sm',
        !disabled && 'active:opacity-50 transition-opacity duration-250 ease-out',
        sizeStyles[size],
        variantStyles[variant],
        className,
      )}
      disabled={disabled}
      {...rest}>
      {leftIcon && <Icon type={leftIcon} className={iconClassName} />}
      <Text variant="small" className={cn('text-white text-inherit', textClassName)}>
        {title}
      </Text>
      {rightIcon && <Icon type={rightIcon} className={iconClassName} />}
      {isLoading && <Loader2 className={cn("size-4 animate-spin", variant === 'secondary' ? 'text-ui_blue' : 'text-white')} />}
    </button>
  );
};

export default Button;
